import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, User, Clock, ArrowLeft, Share2, BookOpen, ChevronRight } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO from '@/components/SEO';
import DOMPurify from 'dompurify';

const BlogPostPage = () => {
  const { postSlug } = useParams();
  const { language, t } = useLanguage();

  // Blog post data - in a real app this would come from CMS or database
  const blogPostData: { [key: string]: any } = {
    'best-grass-types-texas-climate': {
      title: language === 'en' 
        ? 'Best Grass Types for Texas Climate: A Complete Guide' 
        : 'Mejores Tipos de Césped para el Clima de Texas: Guía Completa',
      excerpt: language === 'en'
        ? 'Discover which grass varieties thrive in Texas heat and humidity. Learn about St. Augustine, Bermuda, Zoysia, and more.'
        : 'Descubre qué variedades de césped prosperan en el calor y humedad de Texas. Aprende sobre St. Augustine, Bermuda, Zoysia y más.',
      content: language === 'en' ? `
        <h2>Understanding Texas Climate Challenges</h2>
        <p>Texas' unique climate presents both opportunities and challenges for homeowners looking to establish a beautiful lawn. With scorching summers, unpredictable rainfall, and varying soil conditions across the state, choosing the right grass type is crucial for long-term success.</p>

        <h2>Top Grass Varieties for Texas</h2>

        <h3>1. St. Augustine Grass</h3>
        <p><strong>Best for:</strong> Shade tolerance and heat resistance</p>
        <p>St. Augustine is one of the most popular choices for Texas lawns, and for good reason. This warm-season grass thrives in our climate and offers excellent shade tolerance - a rare trait among warm-season grasses.</p>
        
        <p><strong>Varieties we recommend:</strong></p>
        <ul>
          <li><strong>Raleigh:</strong> Superior cold tolerance, ideal for North Texas</li>
          <li><strong>Palmetto:</strong> Excellent for South Texas, drought resistant</li>
          <li><strong>Floratam:</strong> Great for areas with chinch bug problems</li>
        </ul>

        <h3>2. Bermuda Grass</h3>
        <p><strong>Best for:</strong> High traffic areas and full sun</p>
        <p>Bermuda grass is the champion of durability. If you have kids, pets, or simply high foot traffic, Bermuda can handle it all while maintaining its thick, lush appearance.</p>

        <p><strong>Top variety:</strong> Tifway 419 - The gold standard for sports fields and residential lawns requiring maximum durability.</p>

        <h3>3. Zoysia Grass</h3>
        <p><strong>Best for:</strong> Low maintenance and premium appearance</p>
        <p>While slower to establish, Zoysia offers unmatched beauty and requires less maintenance once mature. It's naturally resistant to weeds and pests.</p>

        <p><strong>Premium choice:</strong> Emerald Zoysia - Dense, carpet-like texture with beautiful color year-round.</p>

        <h2>Installation Tips for Success</h2>
        <p>Timing is everything when installing new sod in Texas. The best times are:</p>
        <ul>
          <li><strong>Spring:</strong> April through May for optimal establishment</li>
          <li><strong>Early Fall:</strong> September through October for cool weather establishment</li>
        </ul>

        <h2>Maintenance Guidelines</h2>
        <p>Each grass type has specific care requirements:</p>
        <ul>
          <li><strong>Watering:</strong> Deep, infrequent watering promotes strong root systems</li>
          <li><strong>Fertilizing:</strong> 3-4 times per year for warm-season grasses</li>
          <li><strong>Mowing:</strong> Never remove more than 1/3 of the blade length</li>
        </ul>

        <h2>Get Professional Installation</h2>
        <p>While DIY installation is possible, professional installation ensures proper soil preparation, correct installation techniques, and optimal results. The Grass Depot's installation team has over 15 years of experience with Texas soil conditions and climate challenges.</p>
      ` : `
        <h2>Entendiendo los Desafíos del Clima de Texas</h2>
        <p>El clima único de Texas presenta tanto oportunidades como desafíos para los propietarios que buscan establecer un césped hermoso. Con veranos abrasadores, lluvia impredecible y condiciones variables del suelo en todo el estado, elegir el tipo correcto de césped es crucial para el éxito a largo plazo.</p>

        <h2>Principales Variedades de Césped para Texas</h2>

        <h3>1. Césped St. Augustine</h3>
        <p><strong>Mejor para:</strong> Tolerancia a la sombra y resistencia al calor</p>
        <p>St. Augustine es una de las opciones más populares para céspedes de Texas, y por buena razón. Este césped de temporada cálida prospera en nuestro clima y ofrece excelente tolerancia a la sombra.</p>
        
        <p><strong>Variedades que recomendamos:</strong></p>
        <ul>
          <li><strong>Raleigh:</strong> Tolerancia superior al frío, ideal para el Norte de Texas</li>
          <li><strong>Palmetto:</strong> Excelente para el Sur de Texas, resistente a la sequía</li>
          <li><strong>Floratam:</strong> Genial para áreas con problemas de chinches</li>
        </ul>

        <h3>2. Césped Bermuda</h3>
        <p><strong>Mejor para:</strong> Áreas de alto tráfico y pleno sol</p>
        <p>El césped Bermuda es el campeón de la durabilidad. Si tienes niños, mascotas, o simplemente alto tráfico peatonal, Bermuda puede manejarlo todo manteniendo su apariencia espesa y exuberante.</p>

        <h3>3. Césped Zoysia</h3>
        <p><strong>Mejor para:</strong> Bajo mantenimiento y apariencia premium</p>
        <p>Aunque más lento para establecerse, Zoysia ofrece belleza inigualable y requiere menos mantenimiento una vez maduro.</p>
      `,
      category: language === 'en' ? 'Lawn Care' : 'Cuidado del Césped',
      author: 'Roberto Martinez',
      publishDate: '2025-03-15',
      readTime: '8 min',
      tags: language === 'en' 
        ? ['St. Augustine', 'Bermuda Grass', 'Texas Landscaping', 'Lawn Care', 'Sod Installation']
        : ['St. Augustine', 'Césped Bermuda', 'Paisajismo Texas', 'Cuidado Césped', 'Instalación Césped']
    },
    'spring-landscaping-checklist-texas': {
      title: language === 'en'
        ? 'Spring Landscaping Checklist for Texas Homeowners'
        : 'Lista de Verificación de Paisajismo de Primavera para Propietarios de Texas',
      excerpt: language === 'en'
        ? 'Get your landscape ready for spring with this comprehensive checklist covering lawn care, plant selection, and maintenance.'
        : 'Prepara tu paisaje para la primavera con esta lista de verificación integral que cubre cuidado del césped, selección de plantas y mantenimiento.',
      content: language === 'en' ? `
        <h2>Preparing Your Texas Landscape for Spring</h2>
        <p>As winter fades and temperatures begin to rise in Texas, it's time to prepare your landscape for the growing season. This comprehensive checklist will help ensure your lawn and garden thrive throughout the spring and summer months.</p>

        <h2>Lawn Care Checklist</h2>
        <h3>March Activities</h3>
        <ul>
          <li><strong>First Mowing:</strong> When grass reaches 3-4 inches, give it the first cut of the season</li>
          <li><strong>Fertilizer Application:</strong> Apply pre-emergent herbicide to prevent summer weeds</li>
          <li><strong>Soil Testing:</strong> Test pH levels and nutrient content</li>
        </ul>

        <h3>April Tasks</h3>
        <ul>
          <li><strong>Overseeding:</strong> Fill in bare spots with matching grass seed</li>
          <li><strong>Irrigation Check:</strong> Test and adjust sprinkler systems</li>
          <li><strong>Edge and Define:</strong> Clean up bed edges and walkways</li>
        </ul>

        <h2>Plant Selection and Care</h2>
        <p>Spring is the perfect time to add new plants to your landscape. Focus on native Texas varieties that are adapted to our climate.</p>

        <h3>Recommended Spring Plantings</h3>
        <ul>
          <li><strong>Trees:</strong> Live Oak, Texas Red Oak, Cedar Elm</li>
          <li><strong>Shrubs:</strong> Esperanza, Pride of Barbados, Texas Sage</li>
          <li><strong>Perennials:</strong> Turk's Cap, Autumn Sage, Mexican Buckeye</li>
        </ul>

        <h2>Mulching Strategy</h2>
        <p>Proper mulching is essential for Texas landscapes. Apply 2-3 inches of quality mulch around trees, shrubs, and flower beds to:</p>
        <ul>
          <li>Retain soil moisture</li>
          <li>Suppress weed growth</li>
          <li>Regulate soil temperature</li>
          <li>Add organic matter to soil</li>
        </ul>

        <h2>Professional Services to Consider</h2>
        <p>While many spring tasks can be handled by homeowners, consider professional help for:</p>
        <ul>
          <li>Large-scale sod installation</li>
          <li>Tree pruning and removal</li>
          <li>Irrigation system installation</li>
          <li>Bulk material delivery and spreading</li>
        </ul>
      ` : `
        <h2>Preparando tu Paisaje de Texas para la Primavera</h2>
        <p>Mientras el invierno se desvanece y las temperaturas comienzan a subir en Texas, es hora de preparar tu paisaje para la temporada de crecimiento.</p>

        <h2>Lista de Verificación para el Cuidado del Césped</h2>
        <h3>Actividades de Marzo</h3>
        <ul>
          <li><strong>Primer Corte:</strong> Cuando el césped alcance 3-4 pulgadas, dale el primer corte de la temporada</li>
          <li><strong>Aplicación de Fertilizante:</strong> Aplica herbicida pre-emergente para prevenir malezas de verano</li>
          <li><strong>Prueba de Suelo:</strong> Prueba los niveles de pH y contenido de nutrientes</li>
        </ul>
      `,
      category: language === 'en' ? 'Seasonal Tips' : 'Consejos Estacionales',
      author: 'Maria Rodriguez',
      publishDate: '2025-03-10',
      readTime: '7 min',
      tags: language === 'en'
        ? ['Spring Preparation', 'Maintenance', 'Texas Gardens', 'Landscaping Tips']
        : ['Preparación Primavera', 'Mantenimiento', 'Jardines Texas', 'Consejos Paisajismo']
    },
    'mulch-benefits-application-guide': {
      title: language === 'en' 
        ? 'The Complete Guide to Mulch: Benefits and Application' 
        : 'Guía Completa del Mantillo: Beneficios y Aplicación',
      excerpt: language === 'en'
        ? 'Learn how proper mulching can improve soil health, retain moisture, and enhance your landscape\'s appearance.'
        : 'Aprende cómo el uso correcto del mantillo puede mejorar la salud del suelo, retener humedad y mejorar la apariencia de tu paisaje.',
      content: language === 'en' ? `
        <h2>What is Mulch?</h2>
        <p>Mulch is a protective layer of organic or inorganic material spread on top of soil around plants, trees, and garden beds. This simple addition to your landscape can provide numerous benefits that enhance both the health and appearance of your outdoor space.</p>

        <h2>Benefits of Mulching</h2>

        <h3>Moisture Conservation</h3>
        <p>Mulch acts as a barrier that reduces water evaporation from the soil surface, helping retain moisture around plant roots. This is especially crucial in Texas where summers can be hot and dry.</p>

        <h3>Temperature Regulation</h3>
        <p>A good layer of mulch insulates the soil, keeping it cooler in summer and warmer in winter. This creates a more stable environment for plant roots.</p>

        <h3>Weed Suppression</h3>
        <p>Mulch blocks sunlight from reaching weed seeds, significantly reducing weed germination and growth in your garden beds.</p>

        <h2>Types of Mulch</h2>

        <h3>Organic Mulches</h3>
        <ul>
          <li><strong>Hardwood Mulch:</strong> Long-lasting, attractive, and great for most applications</li>
          <li><strong>Pine Bark:</strong> Excellent drainage, ideal for acid-loving plants</li>
        </ul>

        <h2>Application Guidelines</h2>
        <p>Apply mulch 2-4 inches deep, but keep it 2-3 inches away from plant stems and tree trunks to prevent pest and disease issues. Refresh organic mulches annually.</p>
      ` : `
        <h2>¿Qué es el Mantillo?</h2>
        <p>El mantillo es una capa protectora de material orgánico o inorgánico esparcido sobre el suelo alrededor de plantas, árboles y camas de jardín.</p>
      `,
      category: language === 'en' ? 'Materials Guide' : 'Guía de Materiales',
      author: 'Carlos Johnson',
      publishDate: '2025-03-05',
      readTime: '6 min',
      tags: language === 'en'
        ? ['Mulch', 'Soil Health', 'Water Conservation', 'Landscape Materials']
        : ['Mantillo', 'Salud del Suelo', 'Conservación Agua', 'Materiales Paisajismo']
    },
    'drought-resistant-landscaping-texas': {
      title: language === 'en'
        ? 'Drought-Resistant Landscaping for Texas Properties'
        : 'Paisajismo Resistente a la Sequía para Propiedades de Texas',
      excerpt: language === 'en'
        ? 'Create a beautiful, low-maintenance landscape that thrives in Texas\' challenging climate conditions.'
        : 'Crea un paisaje hermoso y de bajo mantenimiento que prospere en las condiciones climáticas desafiantes de Texas.',
      content: language === 'en' ? `
        <h2>Planning for Texas Climate</h2>
        <p>Water conservation is becoming increasingly important in Texas. Smart landscaping choices can help you maintain a beautiful outdoor space while reducing water consumption and maintenance needs.</p>

        <h2>Native Plant Selection</h2>
        <p>Native Texas plants are naturally adapted to local climate conditions and require minimal water once established.</p>

        <h3>Recommended Drought-Resistant Plants</h3>
        <ul>
          <li><strong>Texas Sage:</strong> Beautiful purple blooms, extremely drought tolerant</li>
          <li><strong>Esperanza:</strong> Bright yellow flowers, blooms continuously</li>
          <li><strong>Pride of Barbados:</strong> Orange and red flowers, attracts butterflies</li>
        </ul>

        <h2>Water-Efficient Design Principles</h2>
        <h3>Zoning</h3>
        <p>Group plants with similar water needs together to optimize irrigation efficiency.</p>

        <h3>Mulching Strategy</h3>
        <p>Apply 3-4 inches of organic mulch to retain soil moisture and reduce evaporation.</p>

        <h2>Maintenance Tips</h2>
        <p>Deep, infrequent watering encourages deep root growth. Water early morning to reduce evaporation losses.</p>
      ` : `
        <h2>Planificación para el Clima de Texas</h2>
        <p>La conservación del agua se está volviendo cada vez más importante en Texas.</p>
      `,
      category: language === 'en' ? 'Sustainability' : 'Sostenibilidad',
      author: 'Roberto Martinez',
      publishDate: '2025-02-28',
      readTime: '8 min',
      tags: language === 'en'
        ? ['Drought Resistant', 'Water Conservation', 'Native Plants', 'Sustainable Landscaping']
        : ['Resistente Sequía', 'Conservación Agua', 'Plantas Nativas', 'Paisajismo Sostenible']
    },
    'rock-landscaping-design-ideas': {
      title: language === 'en'
        ? 'Rock Landscaping: Design Ideas and Installation Tips'
        : 'Paisajismo con Rocas: Ideas de Diseño y Consejos de Instalación',
      excerpt: language === 'en'
        ? 'Explore creative ways to incorporate rocks and stones into your landscape design for lasting beauty and low maintenance.'
        : 'Explora formas creativas de incorporar rocas y piedras en tu diseño de paisaje para belleza duradera y bajo mantenimiento.',
      content: language === 'en' ? `
        <h2>Why Choose Rock Landscaping?</h2>
        <p>Rock landscaping offers both aesthetic appeal and practical benefits. From drainage solutions to decorative accents, rocks can transform your outdoor space while requiring minimal maintenance.</p>

        <h2>Popular Rock Types for Texas Landscapes</h2>

        <h3>River Rock</h3>
        <p>Smooth, rounded stones perfect for drainage areas and water features. Available in various sizes from pea gravel to large boulders.</p>

        <h3>Decomposed Granite</h3>
        <p>Natural-looking surface material that compacts well for pathways and patios.</p>

        <h2>Design Applications</h2>

        <h3>Drainage Solutions</h3>
        <p>Use river rock in French drains and around downspouts to manage water flow effectively.</p>

        <h3>Decorative Accents</h3>
        <p>Create focal points with boulder groupings or colorful rock gardens.</p>

        <h2>Installation Best Practices</h2>
        <p>Proper preparation is key. Install landscape fabric to prevent weeds, ensure proper depth for stability, and consider drainage requirements for your specific application.</p>
      ` : `
        <h2>¿Por Qué Elegir Paisajismo con Rocas?</h2>
        <p>El paisajismo con rocas ofrece tanto atractivo estético como beneficios prácticos.</p>
      `,
      category: language === 'en' ? 'Design Ideas' : 'Ideas de Diseño',
      author: 'Maria Rodriguez',
      publishDate: '2025-02-20',
      readTime: '5 min',
      tags: language === 'en'
        ? ['Rock Landscaping', 'Design Ideas', 'Low Maintenance', 'Landscape Materials']
        : ['Paisajismo Rocas', 'Ideas Diseño', 'Bajo Mantenimiento', 'Materiales Paisajismo']
    }
  };

  const currentPost = blogPostData[postSlug || ''] || blogPostData['best-grass-types-texas-climate'];

  // Related posts - dynamic based on current post category
  const getRelatedPosts = () => {
    const currentCategory = currentPost.category;
    const allPosts = [
      {
        title: language === 'en' ? 'Spring Landscaping Checklist for Texas Homeowners' : 'Lista de Verificación de Paisajismo de Primavera para Propietarios de Texas',
        slug: 'spring-landscaping-checklist-texas',
        readTime: '7 min',
        category: language === 'en' ? 'Seasonal Tips' : 'Consejos Estacionales'
      },
      {
        title: language === 'en' ? 'The Complete Guide to Mulch: Benefits and Application' : 'Guía Completa del Mantillo: Beneficios y Aplicación',
        slug: 'mulch-benefits-application-guide',
        readTime: '6 min',
        category: language === 'en' ? 'Materials Guide' : 'Guía de Materiales'
      },
      {
        title: language === 'en' ? 'Drought-Resistant Landscaping for Texas Properties' : 'Paisajismo Resistente a la Sequía para Propiedades de Texas',
        slug: 'drought-resistant-landscaping-texas',
        readTime: '8 min',
        category: language === 'en' ? 'Sustainability' : 'Sostenibilidad'
      },
      {
        title: language === 'en' ? 'Rock Landscaping: Design Ideas and Installation Tips' : 'Paisajismo con Rocas: Ideas de Diseño y Consejos de Instalación',
        slug: 'rock-landscaping-design-ideas',
        readTime: '5 min',
        category: language === 'en' ? 'Design Ideas' : 'Ideas de Diseño'
      }
    ];
    
    // Filter out the current post and return 2 related posts
    return allPosts.filter(post => post.slug !== postSlug).slice(0, 2);
  };

  const relatedPosts = getRelatedPosts();

  // SEO structured data for blog post
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": currentPost.title,
    "description": currentPost.excerpt,
    "image": "https://thegrassdepot.com/grass-depot-logo.png",
    "author": {
      "@type": "Person",
      "name": currentPost.author
    },
    "publisher": {
      "@type": "Organization",
      "name": "The Grass Depot LLC",
      "logo": {
        "@type": "ImageObject",
        "url": "https://thegrassdepot.com/grass-depot-logo.png"
      }
    },
    "datePublished": currentPost.publishDate,
    "dateModified": currentPost.publishDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://thegrassdepot.com/${language}/blog/${postSlug}`
    }
  };

  const seoTitle = `${currentPost.title} | The Grass Depot Blog`;
  const seoDescription = currentPost.excerpt;
  const seoKeywords = currentPost.tags.join(', ') + ', Texas landscaping, landscaping tips, lawn care guide';

  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO 
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonical={`/${language}/blog/${postSlug}`}
        structuredData={structuredData}
        language={language}
      />
      <Header />
      
      <main>
      {/* Hablamos Español Banner - English version only */}
      {language === 'en' && (
        <div className="bg-primary text-primary-foreground py-3">
          <div className="container mx-auto px-6 text-center">
            <p className="text-lg font-semibold">
              ¡Hablamos Español! • We speak Spanish • 
              <a href={`/es/blog/${postSlug}`} className="ml-2 underline hover:no-underline">
                Ver artículo en Español
              </a>
            </p>
          </div>
        </div>
      )}
      
      {/* Breadcrumb */}
      <div className="container mx-auto px-6 py-4">
        <div className="flex items-center text-sm text-muted-foreground">
          <Link to={`/${language}`} className="hover:text-primary transition-colors">
            {language === 'en' ? 'Home' : 'Inicio'}
          </Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <Link to={`/${language}/blog`} className="hover:text-primary transition-colors">
            {language === 'en' ? 'Blog' : 'Blog'}
          </Link>
          <ChevronRight className="h-4 w-4 mx-2" />
          <span className="text-foreground line-clamp-1">
            {currentPost.title}
          </span>
        </div>
      </div>

      {/* Article */}
      <article className="py-8">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            
            {/* Back Button */}
            <Link to={`/${language}/blog`} className="inline-flex items-center text-muted-foreground hover:text-primary mb-8 transition-colors">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {language === 'en' ? 'Back to Blog' : 'Volver al Blog'}
            </Link>

            {/* Article Header */}
            <header className="mb-12">
              <div className="mb-6">
                <Badge variant="secondary" className="mb-4">
                  {currentPost.category}
                </Badge>
                <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-6 leading-tight">
                  {currentPost.title}
                </h1>
                <p className="text-xl text-muted-foreground mb-8">
                  {currentPost.excerpt}
                </p>
              </div>
              
              <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground border-b border-border pb-6">
                <div className="flex items-center">
                  <User className="h-4 w-4 mr-2" />
                  {currentPost.author}
                </div>
                <div className="flex items-center">
                  <Calendar className="h-4 w-4 mr-2" />
                  {new Date(currentPost.publishDate).toLocaleDateString(language === 'en' ? 'en-US' : 'es-ES')}
                </div>
                <div className="flex items-center">
                  <Clock className="h-4 w-4 mr-2" />
                  {currentPost.readTime}
                </div>
                <Button variant="outline" size="sm" className="ml-auto">
                  <Share2 className="h-4 w-4 mr-2" />
                  {language === 'en' ? 'Share' : 'Compartir'}
                </Button>
              </div>
            </header>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <div 
                className="article-content"
                dangerouslySetInnerHTML={{ 
                  __html: DOMPurify.sanitize(currentPost.content, {
                    ALLOWED_TAGS: ['h2', 'h3', 'h4', 'p', 'ul', 'ol', 'li', 'strong', 'em', 'a', 'br'],
                    ALLOWED_ATTR: ['href', 'target', 'rel']
                  })
                }}
                style={{
                  lineHeight: '1.7',
                  fontSize: '1.1rem'
                }}
              />
            </div>

            {/* Tags */}
            <div className="mt-12 pt-8 border-t border-border">
              <h3 className="text-lg font-semibold text-foreground mb-4">
                {language === 'en' ? 'Tags:' : 'Etiquetas:'}
              </h3>
              <div className="flex flex-wrap gap-2">
                {currentPost.tags.map((tag: string, index: number) => (
                  <Badge key={index} variant="secondary">
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>

            {/* Author Bio */}
            <div className="mt-12 pt-8 border-t border-border">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                      <User className="h-8 w-8 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {currentPost.author}
                      </h3>
                      <p className="text-muted-foreground">
                        {language === 'en'
                          ? 'Landscaping expert with over 15 years of experience in Texas. Specializes in sod installation, sustainable landscaping, and helping homeowners create beautiful outdoor spaces.'
                          : 'Experto en paisajismo con más de 15 años de experiencia en Texas. Se especializa en instalación de césped, paisajismo sostenible y ayudar a propietarios a crear espacios exteriores hermosos.'
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Related Posts */}
            <div className="mt-12 pt-8 border-t border-border">
              <h3 className="text-2xl font-bold text-foreground mb-6">
                {language === 'en' ? 'Related Articles' : 'Artículos Relacionados'}
              </h3>
              <div className="grid md:grid-cols-2 gap-6">
                {relatedPosts.map((post, index) => (
                  <Card key={index} className="hover:shadow-glow transition-all duration-300">
                    <CardHeader>
                      <CardTitle className="text-lg hover:text-primary transition-colors">
                        <Link to={`/${language}/blog/${post.slug}`}>
                          {post.title}
                        </Link>
                      </CardTitle>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {post.readTime}
                        </div>
                        <Badge variant="secondary" className="text-xs">
                          {post.category}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <Link to={`/${language}/blog/${post.slug}`}>
                        <Button variant="outline" size="sm" className="group">
                          {language === 'en' ? 'Read More' : 'Leer Más'}
                          <ArrowLeft className="ml-2 h-3 w-3 rotate-180 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div className="mt-12 pt-8">
              <Card className="bg-gradient-natural text-white">
                <CardContent className="p-8 text-center">
                  <BookOpen className="h-12 w-12 text-white mx-auto mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-4">
                    {language === 'en' 
                      ? 'Need Professional Landscaping Help?' 
                      : '¿Necesitas Ayuda Profesional de Paisajismo?'
                    }
                  </h3>
                  <p className="text-white/90 mb-6">
                    {language === 'en'
                      ? 'Get expert advice and professional installation services for your landscaping project.'
                      : 'Obtén asesoramiento experto y servicios de instalación profesional para tu proyecto de paisajismo.'
                    }
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to={`/${language}/quote`}>
                      <Button variant="secondary" size="lg">
                        {language === 'en' ? 'Get Free Quote' : 'Obtener Cotización Gratis'}
                      </Button>
                    </Link>
                    <Link to={`/${language}/contact`}>
                      <Button variant="outline" size="lg" className="border-white text-green-800 hover:bg-white hover:text-green-800 font-semibold">
                        {language === 'en' ? 'Contact Us' : 'Contáctanos'}
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPostPage;